import { useMemo, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { articles } from "@/mocks/articles";
import { site } from "@/mocks/site";

const navItems = [
  { label: "首页", to: "/" },
  ...categories.map((c) => ({ label: c.name, to: `/category/${c.slug}` })),
  { label: "常见问题", to: "/faq" },
  { label: "关于本站", to: "/about" },
];

export default function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();

  const results = useMemo(() => {
    const q = query.trim();
    if (!q) return [];
    return articles
      .filter((a) => a.title.includes(q) || a.tags.some((t) => t.includes(q)))
      .slice(0, 5);
  }, [query]);

  const goToArticle = (id: string) => {
    setQuery("");
    setSearchOpen(false);
    navigate(`/article/${id}`);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200/80 bg-background-50/95 backdrop-blur">
      <div className="border-b border-background-200/70 bg-background-100">
        <div className="mx-auto flex max-w-content items-center justify-between px-4 py-1.5 text-[11px] text-foreground-500 md:px-6 md:text-xs">
          <span className="flex items-center gap-2">
            <i className="ri-information-line text-primary-500" aria-hidden="true"></i>
            本站只整理公开信息，并标注可信等级
          </span>
          <span className="hidden items-center gap-4 sm:flex">
            <span className="flex items-center gap-1">
              <i className="ri-checkbox-circle-line text-secondary-500" aria-hidden="true"></i>
              已证实
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-error-warning-line text-accent-600" aria-hidden="true"></i>
              待确认
            </span>
            <Link to="/about" className="cursor-pointer text-primary-600 hover:underline">
              信息分级标准
            </Link>
          </span>
        </div>
      </div>

      <div className="mx-auto flex max-w-content items-center justify-between gap-4 px-4 py-3 md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2.5 cursor-pointer">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="ri-eye-line text-lg" aria-hidden="true"></i>
          </span>
          <span className="leading-tight">
            <span className="block font-heading text-base font-bold tracking-wide text-foreground-950 md:text-lg">
              {site.shortName}
            </span>
            <span className="hidden text-[10px] tracking-widest text-foreground-500 sm:block">
              事件信息梳理
            </span>
          </span>
        </Link>

        <nav aria-label="主导航" className="hidden lg:block">
          <ul className="flex items-center gap-1">
            {navItems.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.to === "/"}
                  className={({ isActive }) =>
                    `block cursor-pointer whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
                      isActive
                        ? "bg-primary-500 text-background-50"
                        : "text-foreground-700 hover:bg-background-200 hover:text-primary-700"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="flex items-center gap-2">
          <div className="relative hidden sm:block">
            <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 focus-within:border-primary-400">
              <i className="ri-search-line text-sm text-foreground-400" aria-hidden="true"></i>
              <input
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setSearchOpen(true);
                }}
                onFocus={() => setSearchOpen(true)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && results.length > 0) goToArticle(results[0].id);
                }}
                placeholder="搜索事件关键词"
                aria-label="站内搜索"
                className="w-28 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400 md:w-36"
              />
            </div>
            {searchOpen && results.length > 0 && (
              <div className="absolute right-0 top-full z-50 mt-2 w-72 overflow-hidden rounded-lg border border-background-200 bg-background-50 shadow-none">
                <ul className="divide-y divide-background-100">
                  {results.map((a) => (
                    <li key={a.id}>
                      <button
                        type="button"
                        onClick={() => goToArticle(a.id)}
                        className="flex w-full cursor-pointer items-start gap-2 px-3 py-2.5 text-left transition-colors hover:bg-background-100"
                      >
                        <i className="ri-file-text-line mt-0.5 text-primary-500" aria-hidden="true"></i>
                        <span className="text-xs leading-snug text-foreground-800 line-clamp-2">
                          {a.title}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <button
            type="button"
            aria-label="打开菜单"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-700 lg:hidden"
          >
            <i className={menuOpen ? "ri-close-line text-lg" : "ri-menu-line text-lg"} aria-hidden="true"></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 lg:hidden">
          <nav aria-label="移动端导航" className="mx-auto max-w-content px-4 py-3">
            <div className="mb-3 flex items-center gap-2 rounded-md border border-background-300 px-3 py-2 sm:hidden">
              <i className="ri-search-line text-sm text-foreground-400" aria-hidden="true"></i>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="搜索事件关键词"
                aria-label="站内搜索"
                className="w-full bg-transparent text-sm outline-none placeholder:text-foreground-400"
              />
            </div>
            {query.trim() && results.length > 0 && (
              <ul className="mb-3 divide-y divide-background-100 rounded-md border border-background-200 sm:hidden">
                {results.map((a) => (
                  <li key={a.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setMenuOpen(false);
                        goToArticle(a.id);
                      }}
                      className="w-full cursor-pointer px-3 py-2 text-left text-xs text-foreground-800"
                    >
                      {a.title}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <ul className="grid grid-cols-2 gap-2">
              {navItems.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.to === "/"}
                    onClick={() => setMenuOpen(false)}
                    className={({ isActive }) =>
                      `block cursor-pointer whitespace-nowrap rounded-md border px-3 py-2.5 text-sm ${
                        isActive
                          ? "border-primary-500 bg-primary-500 text-background-50"
                          : "border-background-200 text-foreground-700"
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      )}
    </header>
  );
}